import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CategoryIcon from '@mui/icons-material/Category';
import HandymanIcon from '@mui/icons-material/Handyman';
import * as departmentApi from '../../api/departmentApi';
import { bust } from '../../utils/apiCache';
import { useSnackbar } from '../../context/SnackbarContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';

export default function DepartmentsTab() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_MASTER_DATA');
  const canDelete = hasPermission('DELETE_MASTER_DATA') || hasPermission('MANAGE_MASTER_DATA');

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try { setDepartments(await departmentApi.getAll()); }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const toggleRow = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => selectedIds.size === departments.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(departments.map((d) => d.id)));
  const allSelected = departments.length > 0 && selectedIds.size === departments.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < departments.length;

  const openCreate = () => { setEditItem(null); setName(''); setDialogOpen(true); };
  const openEdit = (row) => { setEditItem(row); setName(row.name); setDialogOpen(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editItem) { await departmentApi.update(editItem.id, { name }); showSnackbar('Department updated', 'success'); }
      else { await departmentApi.create({ name }); showSnackbar('Department created', 'success'); }
      bust('stats'); // Dashboard's Departments count card reads from this same cache
      setDialogOpen(false); load();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await departmentApi.remove(deleteTarget.id);
      showSnackbar('Department deleted', 'success');
      bust('stats');
      setDeleteTarget(null); load();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      const res = await departmentApi.batchRemove([...selectedIds]);
      showSnackbar(res.message || `${selectedIds.size} department(s) deleted.`, 'success');
      bust('stats');
      load();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  if (loading) return <LoadingSpinner />;

  const colSpan = canDelete ? 6 : 5;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <Box />
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Department</Button>
        )}
      </Box>

      {/* Bulk action toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} row(s) selected</Typography>
          <Button variant="contained" color="error" size="small" startIcon={<DeleteSweepIcon />} onClick={() => setBulkDeleteOpen(true)}>
            Delete Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>Clear</Button>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#F5F5F5' }}>
            {canDelete && (
              <TableCell padding="checkbox">
                <Checkbox size="small" indeterminate={someSelected} checked={allSelected} onChange={toggleAll} />
              </TableCell>
            )}
            <TableCell>#</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CategoryIcon sx={{ fontSize: 14, color: '#1565C0' }} />
                Material Groups
              </Box>
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HandymanIcon sx={{ fontSize: 14, color: '#7B1FA2' }} />
                Accessory Groups
              </Box>
            </TableCell>
            {(canManage || canDelete) && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {departments.map((d, i) => (
            <TableRow key={d.id} hover selected={selectedIds.has(d.id)} sx={{ bgcolor: selectedIds.has(d.id) ? '#FFF8E1 !important' : undefined }}>
              {canDelete && (
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={selectedIds.has(d.id)} onChange={() => toggleRow(d.id)} />
                </TableCell>
              )}
              <TableCell>{i + 1}</TableCell>
              <TableCell sx={{ fontWeight: 500 }}>{d.name}</TableCell>
              <TableCell>
                <Chip
                  label={d._count?.materialGroups ?? 0}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={d._count?.accessoryGroups ?? 0}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </TableCell>
              {(canManage || canDelete) && (
                <TableCell align="right">
                  {canManage && (
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(d)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete (cascades all data)">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(d)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {departments.length === 0 && (
            <TableRow><TableCell colSpan={colSpan} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No departments found</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editItem ? 'Edit Department' : 'New Department'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Department Name" fullWidth value={name}
            onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!name.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Department"
        message={`Delete "${deleteTarget?.name}"?\n\nThis will permanently remove all material groups, materials, accessory groups, accessories, prices, and price history within this department.\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} Department(s)`}
        message={`Delete ${selectedIds.size} selected department(s) and ALL their associated material groups, materials, accessory groups, accessories, prices, and history?\n\nThis cannot be undone.`}
        confirmLabel="Yes, Delete All"
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />
    </Box>
  );
}
